import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/heroSlides";
import { formatCount, getCategoryName, getVideoById } from "@/utils/video";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const total = heroSlides.length;

  useEffect(() => {
    if (paused) return undefined;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % total);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused, total]);

  const go = (next: number) => {
    setIndex((next + total) % total);
  };

  const slide = heroSlides[index];
  const video = getVideoById(slide.id);

  return (
    <section
      aria-label="焦点视频推荐"
      aria-roledescription="carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0].clientX;
      }}
      onTouchEnd={(event) => {
        if (touchStartX.current === null) return;
        const delta = event.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 40) go(index + (delta < 0 ? 1 : -1));
        touchStartX.current = null;
      }}
      className="relative h-[300px] w-full overflow-hidden rounded-lg bg-primary-900 sm:h-[360px] lg:h-full lg:min-h-[420px]"
    >
      {heroSlides.map((item, itemIndex) => (
        <div
          key={item.id}
          aria-hidden={itemIndex !== index}
          className={`absolute inset-0 transition-opacity duration-700 ease-out ${
            itemIndex === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={item.image}
            alt={`${item.eyebrow} 焦点大图`}
            title="色色视频焦点推荐"
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/92 via-primary-950/55 to-primary-950/25" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/80 via-primary-950/20 to-transparent" />
        </div>
      ))}

      <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-7">
        <div className="max-w-xl animate-fade-up" key={slide.id}>
          <p className="mb-2 inline-flex items-center gap-2 rounded-full bg-accent-500/95 px-2.5 py-1 font-label text-[10px] font-semibold uppercase tracking-[0.14em] text-primary-950">
            <i className="ri-flashlight-fill text-[11px]" aria-hidden="true" />
            {slide.eyebrow}
          </p>
          {video ? (
            <>
              <h1 className="text-2xl font-semibold leading-tight text-background-50 md:text-[34px]">
                {video.title}
              </h1>
              <p className="mt-2 line-clamp-2 max-w-lg text-xs leading-relaxed text-background-200 md:text-sm">
                {slide.tagline}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 font-label text-[11px] text-background-200">
                <span className="rounded bg-background-50/15 px-2 py-0.5">
                  {getCategoryName(video.category)}
                </span>
                <span>{video.year}</span>
                <span className="text-background-50/40">/</span>
                <span>{video.region}</span>
                <span className="text-background-50/40">/</span>
                <span>{video.duration}</span>
                <span className="text-background-50/40">/</span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-eye-line" aria-hidden="true" />
                  {formatCount(video.views)}
                </span>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Link
                  to={`/detail/${video.id}`}
                  className="focus-ring inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-xs font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                >
                  查看详情
                  <i className="ri-arrow-right-line text-sm" aria-hidden="true" />
                </Link>
                <Link
                  to={`/category/${video.category}`}
                  className="focus-ring inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/35 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-background-50/10"
                >
                  同分类更多
                </Link>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一张焦点图"
        className="focus-ring absolute left-3 top-1/2 hidden h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-background-50/30 bg-primary-950/40 text-background-50 transition-colors hover:bg-primary-950/70 sm:flex md:left-4 md:h-10 md:w-10"
      >
        <i className="ri-arrow-left-s-line text-xl" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一张焦点图"
        className="focus-ring absolute right-3 top-1/2 hidden h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-background-50/30 bg-primary-950/40 text-background-50 transition-colors hover:bg-primary-950/70 sm:flex md:right-4 md:h-10 md:w-10"
      >
        <i className="ri-arrow-right-s-line text-xl" aria-hidden="true" />
      </button>

      <div className="absolute bottom-5 right-5 flex items-center gap-1.5 md:bottom-7 md:right-7">
        {heroSlides.map((item, itemIndex) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setIndex(itemIndex)}
            aria-label={`切换到第 ${itemIndex + 1} 张焦点图`}
            aria-current={itemIndex === index}
            className={`h-1.5 cursor-pointer rounded-full transition-all ${
              itemIndex === index ? "w-6 bg-accent-500" : "w-1.5 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>

      <div className="absolute bottom-5 left-5 hidden font-label text-[10px] uppercase tracking-[0.2em] text-background-300 md:block md:bottom-7 md:left-7">
        {`0${index + 1} / 0${total}`}
      </div>
    </section>
  );
}